/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

import { EdsIcon } from "@components/elements/atoms";
import { IndustryFragment } from "@cosmos/hygraph-schema";
import { useMessages } from "@epic-mw/i18n";
import { css, cx } from "@panda/css";
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";

interface Props {
	industryPlaceholder?: string | null;
	industries?: IndustryFragment[];
	setSelectedIndustry: Dispatch<SetStateAction<IndustryFragment | null>>;
	selectedIndustry?: IndustryFragment | null;
	validIndustrySelected?: boolean;
}

const styles = css({
	display: "inline-flex",
	position: "relative",
	textStyle: "uiMD",
});

const placeholderStyles = css({
	display: "flex",
	gap: "eds.dimension.4",
	alignItems: "center",
	cursor: "pointer",
	color: "eds.text.link.primary.default",
	textDecoration: "underline",
	textStyle: "headingXL",
	transition: "color 0.12s token(animations.eds.easing.easeInOutQuad)",
	"& svg": {
		transition: "transform 0.12s token(animations.eds.easing.easeInOutQuad)",
	},
	"&.open svg": {
		transform: "rotate(180deg)",
	},
	_hover: {
		color: "eds.fill.primary.hover",
	},
});

const menu = css({
	position: "absolute",
	zIndex: "5",
	top: "100%",
	left: "50%",
	transform: "translateX(-50%)",
	marginTop: "eds.dimension.16",
	borderRadius: "eds.dimension.16",
	display: "flex",
	width: "330px",
	flexDirection: "column",
	alignItems: "flex-start",
	background: "eds.background.elevated.high.default",
	boxShadow: "eds.bottom.md",
	gap: "eds.dimension.8",
	padding: "eds.dimension.8",
	backdropFilter: "blur(50px)",
	border: "1px solid",
	borderColor: "eds.border.subdued",
});

const menuItem = css({
	display: "block",
	paddingBlock: "eds.dimension.10",
	paddingInline: "eds.dimension.16",
	cursor: "pointer",
	w: "full",
	textAlign: "start",
	borderRadius: "eds.dimension.8",
	_hover: {
		background: "eds.background.elevated.high.hover",
	},
});

export const IndustrySelector = ({
	industryPlaceholder,
	industries,
	setSelectedIndustry,
	selectedIndustry,
	validIndustrySelected,
}: Props): React.JSX.Element | null => {
	const messages = useMessages();
	const requiredText = messages["epic.cosmos.required.field.text"] || "";

	const [showMenu, setShowMenu] = useState(false);

	const onClickPlaceholder = useCallback(() => {
		setShowMenu(!showMenu);
	}, [showMenu, setShowMenu]);

	const onKeyDownPlaceholder = useCallback(
		(e: React.KeyboardEvent<HTMLButtonElement>) => {
			if (e.key === "Enter" || e.key === " ") {
				setShowMenu(!showMenu);
				e.preventDefault();
			}
		},
		[showMenu, setShowMenu],
	);

	const onClickIndustry = useCallback(
		(industry: IndustryFragment) => {
			if (industry && industry.salesForceId) {
				setSelectedIndustry(industry);
				setShowMenu(false);
			}
		},
		[setSelectedIndustry, setShowMenu],
	);

	const componentRef = useRef<HTMLDivElement>(null);

	//handle body click
	const bodyClick = useCallback(
		(e: MouseEvent) => {
			if (!showMenu) return;
			if (componentRef.current && e.target instanceof HTMLElement && !componentRef.current.contains(e.target)) {
				// Click outside the component
				setShowMenu(false);
			}
		},
		[showMenu, setShowMenu],
	);

	//click away listener
	useEffect(() => {
		document.body.addEventListener("click", bodyClick);
		return () => {
			document.body.removeEventListener("click", bodyClick);
		};
	}, [bodyClick]);

	// handle ESC key
	const handleEsc = useCallback(
		(e: KeyboardEvent) => {
			if (!showMenu) return;
			if (e.key === "Escape") {
				setShowMenu(false);
			}
		},
		[showMenu, setShowMenu],
	);

	//escape listener
	useEffect(() => {
		window.addEventListener("keydown", handleEsc);

		return () => {
			window.removeEventListener("keydown", handleEsc);
		};
	}, [handleEsc]);

	if (!industryPlaceholder || !industries?.length) {
		return null;
	}

	return (
		<div className={styles} ref={componentRef}>
			<button
				className={cx("industry-menu", showMenu ? "open" : "closed", placeholderStyles)}
				title={validIndustrySelected && selectedIndustry?.label ? selectedIndustry.label : requiredText}
				onClick={onClickPlaceholder}
				onKeyDown={onKeyDownPlaceholder}
			>
				<span>{selectedIndustry?.label || industryPlaceholder}</span>
				<EdsIcon icon="ChevronDown" size="lg" />
			</button>

			{showMenu ? (
				<ul className={cx("industry-menu", menu)}>
					{industries.map((industry) => {
						return (
							<li key={industry.salesForceId} className={css({ w: "full" })}>
								<button
									className={menuItem}
									onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
										if (e.key === "Enter" || e.key === " ") {
											onClickIndustry(industry);
											e.preventDefault();
										}
									}}
									onClick={(e: React.PointerEvent<HTMLButtonElement>) => {
										onClickIndustry(industry);
										e.preventDefault();
									}}
								>
									{industry.label}
								</button>
							</li>
						);
					})}
				</ul>
			) : null}
		</div>
	);
};
